import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Area } from 'react-easy-crop';
import { ImageCropper } from '@/components/ImageCropper/ImageCropper';
import { Button } from '@/components/ui/Button';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type { Person, PreparedProfilePicture } from '@/types/person';
import type { ProfilePicturePending } from './types';
import { areaToCropRect, cropImageToBlob } from './utils';

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = 'image/jpeg,image/png,image/webp';

type Status = 'idle' | 'uploading' | 'cropping' | 'preview';

interface ProfilePictureSectionProps {
    person: Person;
    pending: ProfilePicturePending | null;
    onPendingChange: (pending: ProfilePicturePending | null) => void;
    onInProgressChange: (inProgress: boolean) => void;
}

export function ProfilePictureSection({
    person,
    pending,
    onPendingChange,
    onInProgressChange,
}: ProfilePictureSectionProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const preparedIdRef = useRef<string | null>(null);

    const discardPrepared = () => {
        const pendingId = preparedIdRef.current;
        preparedIdRef.current = null;
        if (pendingId) {
            void EngineService.discardPersonProfilePicture(
                person.id,
                pendingId,
            ).catch(() => {});
        }
    };

    useEffect(
        () => () => {
            discardPrepared();
        },
        [person.id],
    );

    const [status, setStatus] = useState<Status>('idle');
    const [prepared, setPrepared] = useState<PreparedProfilePicture | null>(
        null,
    );
    const [cropArea, setCropArea] = useState<Area | null>(null);
    const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
    const [uploadPercent, setUploadPercent] = useState(0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!croppedPreview?.startsWith('blob:')) return;
        return () => URL.revokeObjectURL(croppedPreview);
    }, [croppedPreview]);

    const isUnresolved = status === 'uploading' || status === 'cropping';

    useEffect(() => {
        onInProgressChange(isUnresolved);
        return () => onInProgressChange(false);
    }, [isUnresolved, onInProgressChange]);

    const reset = () => {
        discardPrepared();
        setStatus('idle');
        setPrepared(null);
        setCropArea(null);
        setCroppedPreview(null);
        setUploadPercent(0);
        setError(null);
        onPendingChange(null);
    };

    const handleFilePicked = async (file: File) => {
        if (file.size > MAX_UPLOAD_BYTES) {
            setError(t('persons.profilePicture.tooLarge'));
            return;
        }
        setError(null);
        setStatus('uploading');
        setUploadPercent(0);
        try {
            const result = await EngineService.preparePersonProfilePicture(
                person.id,
                file,
                setUploadPercent,
            );
            discardPrepared();
            preparedIdRef.current = result.pendingId;
            setPrepared(result);
            setStatus('cropping');
        } catch {
            setError(t('persons.profilePicture.uploadError'));
            setStatus('idle');
        }
    };

    const handleConfirmCrop = async () => {
        if (!prepared || !cropArea) return;
        const crop = areaToCropRect(cropArea);
        setError(null);
        const previewBlob = await cropImageToBlob(
            prepared.originalUrl,
            crop,
        ).catch(() => null);
        setCroppedPreview(
            previewBlob
                ? URL.createObjectURL(previewBlob)
                : prepared.originalUrl,
        );
        onPendingChange({ type: 'set', pendingId: prepared.pendingId, crop });
        setStatus('preview');
    };

    const backToCropping = () => {
        setCroppedPreview(null);
        onPendingChange(null);
        setStatus('cropping');
    };

    const openFilePicker = () => fileInputRef.current?.click();

    const currentImage =
        pending?.type === 'set'
            ? croppedPreview
            : person.hasProfilePicture
              ? person.imageUrl
              : null;

    return (
        <div className="flex flex-col gap-3">
            <span className="text-sm-medium text-white-100">
                {t('persons.profilePicture.title')}
            </span>

            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_MIME_TYPES}
                className="hidden"
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void handleFilePicked(file);
                }}
            />

            {status === 'uploading' && (
                <p className="text-sm text-grey-200">
                    {t('persons.profilePicture.uploading', {
                        percent: uploadPercent,
                    })}
                </p>
            )}

            {status === 'cropping' && prepared && (
                <div className="flex flex-col gap-3">
                    <ImageCropper
                        image={prepared.originalUrl}
                        cropShape="rect"
                        onCropAreaChange={setCropArea}
                    />
                    <div className="flex gap-2">
                        <Button variant="tertiary" onClick={reset}>
                            {t('persons.profilePicture.cancel')}
                        </Button>
                        <Button onClick={() => void handleConfirmCrop()}>
                            {t('persons.profilePicture.continue')}
                        </Button>
                    </div>
                </div>
            )}

            {status === 'preview' && croppedPreview && (
                <div className="flex flex-col gap-3">
                    <img
                        src={croppedPreview}
                        alt=""
                        className="aspect-square w-full rounded-lg object-cover"
                    />
                    {pending?.type === 'set' && (
                        <p className="text-sm">
                            {t('persons.profilePicture.willApplyOnSave')}
                        </p>
                    )}
                    <div className="flex gap-2">
                        <Button variant="tertiary" onClick={reset}>
                            {t('persons.profilePicture.cancel')}
                        </Button>
                        <Button variant="secondary" onClick={backToCropping}>
                            {t('persons.profilePicture.recrop')}
                        </Button>
                    </div>
                </div>
            )}

            {status === 'idle' && (
                <div className="flex items-center gap-4">
                    {currentImage && pending?.type !== 'remove' ? (
                        <img
                            src={currentImage}
                            alt=""
                            className="size-16 rounded-lg object-cover"
                        />
                    ) : (
                        <div className="flex size-16 items-center justify-center rounded-lg bg-grey-700 text-xs text-grey-300">
                            {pending?.type === 'remove'
                                ? t('persons.profilePicture.removed')
                                : t('persons.profilePicture.none')}
                        </div>
                    )}
                    <div className="flex flex-col gap-2">
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={openFilePicker}
                        >
                            {currentImage && pending?.type !== 'remove'
                                ? t('persons.profilePicture.replace')
                                : t('persons.profilePicture.upload')}
                        </Button>
                        {(person.hasProfilePicture ||
                            pending?.type === 'set') &&
                            pending?.type !== 'remove' && (
                                <Button
                                    variant="ghost"
                                    size="small"
                                    onClick={() =>
                                        onPendingChange({ type: 'remove' })
                                    }
                                >
                                    {t('persons.profilePicture.remove')}
                                </Button>
                            )}
                        {pending?.type === 'remove' && (
                            <Button
                                variant="ghost"
                                size="small"
                                onClick={() => onPendingChange(null)}
                            >
                                {t('persons.profilePicture.undo')}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {error && <p className="text-sm text-danger-500">{error}</p>}
        </div>
    );
}
